/**
 * The bulk-write audit contract (docs/01 §7, ADR-67, punch item 7.1; 0071).
 *
 * Matt's requirement: a bulk import writes to the module's own history AND the
 * global log, per record, carrying user, timestamp, module and old → new, and
 * tagged `Bulk Import Process by {username}`.
 *
 * THE TAG LIVES HERE AND NOWHERE ELSE. It is composed at read time from the
 * batch's kind and the ledger row's own actor, never stored — a stored sentence
 * would be a second copy of who did it, free to drift from the column that
 * actually records it. And it lives in `@propos/shared` for the same reason
 * `audit-catalog.ts` and `field-labels.ts` do: the record's History tab and the
 * admin ledger both render it, and two copies would eventually word one batch
 * two ways (docs/01 §7).
 */

/**
 * Only kinds with a caller.
 *
 * `sweep` has one today — the offboarding release, which takes every action item
 * a person owns off them at once. `import` is named by the requirement itself
 * and gets its writer with the wizard (7.2); its template is the deliverable
 * this item exists to settle.
 *
 * 3D.4's retroactive casing fix is a bulk write and is NOT an import, which is
 * exactly why the tag is per kind rather than one string: rendering "Bulk Import
 * Process by Rob" over a normalisation would put a false statement in an
 * append-only ledger. `normalization` was deliberately absent until it had a
 * writer; 3D.4 is that writer, and 0073 widened 0071's CHECK in the same change.
 */
export const BULK_BATCH_KINDS = ['import', 'sweep', 'normalization'] as const;
export type BulkBatchKind = (typeof BULK_BATCH_KINDS)[number];

/**
 * `{name}` is the ONLY placeholder, and it is the human who ran the batch —
 * `actor_user_id`, which under View As is the administrator driving and not the
 * account they were looking through (docs/01 §6). "by {username}" answers "who
 * do I go and ask", and that is never the impersonated user.
 *
 * `import`'s wording is Matt's, verbatim, and is not to be tidied: it is the
 * phrase the people reading these logs asked for.
 */
export const BULK_TAG_TEMPLATE: Record<BulkBatchKind, string> = {
  import: 'Bulk Import Process by {name}',
  sweep: 'Access sweep by {name}',
  // "Reviewed" is the load-bearing word. 3D.4 refuses to apply anything nobody
  // ticked, so the tag on a renamed record should say a person chose it —
  // otherwise the next reader assumes a script did, which is the exact
  // suspicion the punch row exists to prevent.
  normalization: 'Name capitalization reviewed by {name}',
};

/** What the reader sees on a tagged history row. */
export const BULK_KIND_LABEL: Record<BulkBatchKind, string> = {
  import: 'Bulk import',
  sweep: 'Access sweep',
  normalization: 'Name capitalization',
};

export function isBulkBatchKind(value: unknown): value is BulkBatchKind {
  return typeof value === 'string' && (BULK_BATCH_KINDS as readonly string[]).includes(value);
}

/**
 * The tag for one history row.
 *
 * A missing actor name is rendered as "somebody" rather than as an empty gap or
 * a uuid: `app_actor_display_name` returns null for an account the reader cannot
 * resolve, and "Bulk Import Process by " reads as a bug while "…by somebody"
 * reads as the truth — the run happened, and this reader cannot see whose it
 * was. Never the id, per ADR-39.
 */
export function bulkTag(kind: BulkBatchKind, actorName: string | null): string {
  return BULK_TAG_TEMPLATE[kind].replace('{name}', actorName ?? 'somebody');
}

/** The batch header, as the admin ledger reads it. */
export interface BulkBatchRow {
  readonly id: string;
  readonly kind: BulkBatchKind;
  /** An `audit_events.entity_type`, not a free-text module name. */
  readonly module: string;
  readonly sourceLabel: string | null;
  readonly startedAt: string;
  readonly startedByName: string | null;
  readonly finishedAt: string | null;
  /** Ledger rows written, including the batch's own completion event. */
  readonly eventCount: number;
  readonly createdCount: number;
  readonly updatedCount: number;
  /** Records the run looked at and did not change — no ledger row, by definition. */
  readonly skippedCount: number;
}
